import React from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CTABanner from "../components/CTABanner";
import ConventionalTowerHoistPage from "./ConventionalTowerHoistPage";
import MultiFunctionalTowerHoistPage from "./MultiFunctionalTowerHoistPage";
import PassengerCumMaterialHoistPage from "./PassengerCumMaterialHoistPage";
import MonkeyLiftPage from "./MonkeyLiftPage";
import RopeSuspendedPlatformPage from "./RopeSuspendedPlatformPage";
import BarBendingMachinePage from "./BarBendingMachinePage";
import BarCuttingMachinePage from "./BarCuttingMachinePage";
import ConcreteMixerPage from "./ConcreteMixerPage";
import ReversibleDrumMixerPage from "./ReversibleDrumMixerPage";
import WheelBarrowPage from "./WheelBarrowPage";
import { PRODUCTS } from "../data/products";
import { motion } from "framer-motion";
import { ArrowRight, ChevronRight, CheckCircle2, Download, Phone, Wrench, ShieldCheck, Award, Headphones, Building2, Factory } from "lucide-react";
import Seo, { buildBreadcrumb, buildProductSchema } from "../components/Seo";
import { PRODUCT_SEO } from "../data/seo";
import PicImg from "../components/PicImg";

function ProductSeo({ slug }) {
  const product = PRODUCTS.find((p) => p.slug === slug || (slug === "suspended-platform" && p.slug === "rope-suspended-platform") || (slug === "reversible-drum-mixer" && p.slug === "reversible-drum-concrete-mixer"));
  if (!product) return null;
  const canonicalSlug = product.slug;
  const seo = PRODUCT_SEO[canonicalSlug] || {};
  return (
    <Seo
      title={seo.title || `${product.name} | LIFTILE`}
      description={seo.description || product.description}
      keywords={seo.keywords}
      path={`/products/${canonicalSlug}`}
      image={product.image}
      type="product"
      jsonLdId={`product-${canonicalSlug}`}
      jsonLd={[
        buildBreadcrumb([
          { name: "Home", path: "/" },
          { name: "Products", path: "/products" },
          { name: product.name, path: `/products/${canonicalSlug}` },
        ]),
        buildProductSchema({
          name: product.name,
          description: seo.description || product.description,
          image: product.image,
          sku: `LIFTILE-${canonicalSlug.toUpperCase()}`,
          category: product.category,
          specs: product.specifications,
        }),
      ]}
    />
  );
}

export default function ProductPage() {
  const { slug } = useParams();

  // Dedicated premium pages for refined products.
  if (slug === "conventional-tower-hoist") {
    return (<><ProductSeo slug={slug} /><ConventionalTowerHoistPage /></>);
  }
  if (slug === "multi-functional-tower-hoist") {
    return (<><ProductSeo slug={slug} /><MultiFunctionalTowerHoistPage /></>);
  }
  if (slug === "passenger-cum-material-hoist") {
    return (<><ProductSeo slug={slug} /><PassengerCumMaterialHoistPage /></>);
  }
  if (slug === "monkey-lift") {
    return (<><ProductSeo slug={slug} /><MonkeyLiftPage /></>);
  }
  if (slug === "rope-suspended-platform" || slug === "suspended-platform") {
    return (<><ProductSeo slug={slug} /><RopeSuspendedPlatformPage /></>);
  }
  if (slug === "bar-bending-machine") {
    return (<><ProductSeo slug={slug} /><BarBendingMachinePage /></>);
  }
  if (slug === "bar-cutting-machine") {
    return (<><ProductSeo slug={slug} /><BarCuttingMachinePage /></>);
  }
  if (slug === "concrete-mixer") {
    return (<><ProductSeo slug={slug} /><ConcreteMixerPage /></>);
  }
  if (slug === "reversible-drum-concrete-mixer" || slug === "reversible-drum-mixer") {
    return (<><ProductSeo slug={slug} /><ReversibleDrumMixerPage /></>);
  }
  if (slug === "wheel-barrow") {
    return (<><ProductSeo slug={slug} /><WheelBarrowPage /></>);
  }

  const product = PRODUCTS.find((p) => p.slug === slug);

  if (!product) {
    return (
      <>
        <Navbar />
        <div className="pt-32 min-h-[60vh] container-x text-center">
          <h1 className="display-2 text-liftile-cream">Product Not Found</h1>
          <Link to="/products" className="btn-primary mt-6 inline-flex">Back To Products</Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ProductSeo slug={slug} />
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-24 bg-liftile-black border-b border-white/5">
        <div className="container-x py-5 flex items-center gap-2 text-xs text-liftile-cream/50 font-heading uppercase tracking-wider">
          <Link to="/" className="hover:text-liftile-orange">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/products" className="hover:text-liftile-orange">Products</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-liftile-orange">{product.name}</span>
        </div>
      </div>

      {/* Hero */}
      <section data-testid="product-hero" className="relative bg-liftile-black overflow-hidden">
        <div className="absolute inset-0 industrial-grid opacity-25" />
        <div className="container-x relative grid lg:grid-cols-2 gap-12 items-center py-16 md:py-20">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}>
            <div className="heading-eyebrow mb-3">{product.category}</div>
            <h1 className="display-1 text-liftile-cream">
              {product.tagline.split(".").map((line, i) =>
                line.trim() ? (
                  <span key={i} className="block">
                    {i === 1 ? <span className="text-liftile-orange">{line}.</span> : `${line}.`}
                  </span>
                ) : null
              )}
            </h1>
            <p className="mt-6 text-liftile-cream/70 max-w-xl font-body">{product.description}</p>

            <div className="flex flex-wrap gap-3 mt-7">
              {product.features.slice(0, 5).map((f) => (
                <div key={f.title} className="flex items-center gap-2 text-xs font-heading uppercase tracking-wider text-liftile-cream/80 border border-white/15 px-3 py-2">
                  <CheckCircle2 className="w-3 h-3 text-liftile-orange" /> {f.title}
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/contact" data-testid="product-enquire-btn" className="btn-primary">Enquire Now <ArrowRight className="w-4 h-4" /></Link>
              <button data-testid="product-brochure-btn" className="btn-outline">Download Brochure <Download className="w-4 h-4" /></button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative">
            <div className="aspect-square overflow-hidden border border-white/10">
              <PicImg src={product.image} alt={product.name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-liftile-orange p-6 max-w-[280px]">
              <div className="font-display text-3xl text-white leading-none">{product.specifications[1]?.value || ""}</div>
              <div className="text-xs uppercase tracking-wider text-white/80 mt-1 font-heading">{product.specifications[1]?.label || "Capacity"}</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="bg-liftile-cream-light py-20 md:py-24 text-liftile-matte-black">
        <div className="container-x">
          <div className="grid lg:grid-cols-2 gap-12 mb-12">
            <div>
              <div className="heading-eyebrow mb-3">Key Features</div>
              <h2 className="display-2">
                Engineered For Safety<br /><span className="text-liftile-orange">Built For Performance</span>
              </h2>
            </div>
            <p className="font-body text-liftile-mid-gray/85 self-end">Every component is designed to deliver unmatched safety, reliability and long service life even under the most demanding conditions.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {product.features.map((f) => (
              <div key={f.title} className="bg-white p-6 border-t-2 border-liftile-orange">
                <Wrench className="w-7 h-7 text-liftile-orange mb-4" />
                <h3 className="font-heading text-sm uppercase font-semibold tracking-wider mb-2">{f.title}</h3>
                <p className="text-sm text-liftile-mid-gray/75 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specifications */}
      <section className="bg-liftile-cream py-20 md:py-24 text-liftile-matte-black">
        <div className="container-x grid lg:grid-cols-[1fr_360px] gap-10">
          <div>
            <div className="heading-eyebrow mb-3">Technical Specifications</div>
            <h2 className="display-2">Detailed <span className="text-liftile-orange">Specifications</span></h2>

            <div className="mt-8 grid sm:grid-cols-2 gap-x-10">
              {product.specifications.map((s, i) => (
                <div key={s.label + i} className="flex justify-between gap-4 py-3 border-b border-liftile-mid-gray/15">
                  <span className="font-heading text-xs uppercase tracking-wider text-liftile-mid-gray/70 self-center">{s.label}</span>
                  <span className="font-body text-sm text-liftile-matte-black text-right">{s.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-liftile-matte-black text-liftile-cream p-8 self-start">
            <div className="font-display text-3xl leading-tight">
              Download<br />Technical Sheet
            </div>
            <p className="text-sm text-liftile-cream/60 mt-3">Get detailed technical information, drawings and dimensions.</p>
            <button data-testid="product-download-pdf" className="btn-primary mt-6 w-full justify-center">Download PDF <Download className="w-4 h-4" /></button>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="bg-liftile-black py-20 md:py-24">
        <div className="container-x">
          <div className="heading-eyebrow mb-3">Applications</div>
          <h2 className="display-2 text-liftile-cream">Where It <span className="text-liftile-orange">Works Best</span></h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {product.applications.map((a) => (
              <div key={a} className="flex items-start gap-4 p-5 border border-white/10 hover:border-liftile-orange transition">
                <Building2 className="w-6 h-6 text-liftile-orange flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-heading text-sm uppercase tracking-wider text-liftile-cream">{a}</div>
                  <div className="text-xs text-liftile-cream/50 mt-1">Reliable lifting solution for {a.toLowerCase()}.</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTABanner
        headline="Need the Right Machine for Your Project?"
        description="Our engineering team is ready to help you choose the ideal solution for your construction requirements."
      />

      <Footer />
    </>
  );
}
